import React, { useEffect, useRef, useState } from 'react';
import { Award, BookOpen, Brain, Target, Clock, ClipboardCheck, CheckCircle2, ChevronLeft, ChevronRight, Lock, MessageSquare, CheckCircle } from 'lucide-react';
import { ContentItem, ModuleContentTree } from '@/types/ai-learn.types';
import { DetailedProgress } from '@/services/tracking.service';
import type { CourseCompletionRequirementStatus } from '@/services/course.service';
import ContentDisplay from './ContentDisplay';
import ForumThreads from './ForumThreads';

interface UnifiedPlayerViewProps {
  viewType: 'course' | 'module' | 'content' | 'none';
  course: any;
  courseId: string;
  selectedModule?: any;
  moduleContent?: ModuleContentTree | null;
  contentItem?: ContentItem | null;
  loadingModuleContent?: boolean;
  isLearner?: boolean; // Prop to determine if user is a learner (show forum) or admin (hide forum)
  isScormCourse?: boolean; // SCORM courses: hide estimated time (completion is CMI/manual), show only spent/completed time

  // Content display props
  currentVideoScene?: number;
  currentAudioScene?: number;
  isVideoPlaying?: boolean;
  isAudioPlaying?: boolean;
  videoPlaybackRate?: number;
  audioPlaybackRate?: number;
  videoVolume?: number;
  audioVolume?: number;
  autoPlayNext?: boolean;

  // Event handlers
  onVideoSceneChange?: (scene: number) => void;
  onAudioSceneChange?: (scene: number) => void;
  onVideoPlayPause?: () => void;
  onVideoPlayStateChange?: (playing: boolean) => void;
  /** Position carried across a language switch. See CustomVideoPlayer. */
  onVideoProgressRatio?: (ratio: number, playing: boolean) => void;
  videoResumeRatio?: number;
  videoResumeAutoPlay?: boolean;
  onAudioPlayPause?: () => void;
  onVideoPlaybackRateChange?: () => void;
  onAudioPlaybackRateChange?: () => void;
  onVideoVolumeChange?: (volume: number) => void;
  onAudioVolumeChange?: (volume: number) => void;
  onAutoPlayToggle?: () => void;
  onSelectModule?: (module: any) => void;
  onSelectContentItem?: (item: ContentItem) => void;
  onContentComplete?: (contentItemId: string) => void;
  onProgressChange?: (progressPercentage: number) => void;
  progressData?: DetailedProgress | null;
  /** Assessments that must be passed for the course to complete, with pass state. */
  completionRequirements?: CourseCompletionRequirementStatus | null;
  /** Fired when a learner submits an assessment, so completion can be re-checked. */
  onAssessmentSubmitted?: () => void;
  /** Below lg the sidebar is hidden; these open it (course content sheet / AI assistant). */
  onOpenCourseContent?: () => void;
  onOpenAIAssistant?: () => void;
  /** Step to the previous / next item in course order. Omitted at either end. */
  onPrevious?: () => void;
  onNext?: () => void;
  /** The next item is locked; Next shows a lock and onNext explains why. */
  nextLocked?: boolean;
}

// TabButton component for styling consistency
const TabButton = ({ label, icon, isActive, onClick }: { label: string; icon: React.ReactNode; isActive: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2 ${
      isActive
        ? 'border-brand-indigo-600 text-brand-indigo-600'
        : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-800'
    }`}
  >
    {icon}
    {label}
  </button>
);

// Format seconds to HH:MM:SS
const formatSecondsToHMS = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Shows actual time spent / completed time (green when completed, blue while in progress).
// Used in place of estimated time for SCORM courses (whose completion is CMI/manual-based,
// not time-based) — and alongside estimated time elsewhere.
const TimeSpentBadge = ({ seconds, completed }: { seconds?: number; completed?: boolean }) => {
  if (!seconds || seconds <= 0) return null;
  return (
    <div className={`flex items-center gap-1.5 text-xs ${completed ? 'text-green-600' : 'text-blue-500'}`}>
      {completed ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
      <span>{completed ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(seconds)}</span>
    </div>
  );
};


export default function UnifiedPlayerView({
  viewType,
  course,
  courseId,
  selectedModule,
  moduleContent,
  contentItem,
  loadingModuleContent,
  isLearner = false,
  isScormCourse = false,
  currentVideoScene,
  currentAudioScene,
  isVideoPlaying,
  isAudioPlaying,
  videoPlaybackRate,
  audioPlaybackRate,
  videoVolume,
  audioVolume,
  autoPlayNext,
  onVideoSceneChange,
  onAudioSceneChange,
  onVideoPlayPause,
  onVideoPlayStateChange,
  onVideoProgressRatio,
  videoResumeRatio,
  videoResumeAutoPlay,
  onAudioPlayPause,
  onVideoPlaybackRateChange,
  onAudioPlaybackRateChange,
  onVideoVolumeChange,
  onAudioVolumeChange,
  onAutoPlayToggle,
  onSelectModule,
  onSelectContentItem,
  onContentComplete,
  onProgressChange,
  progressData,
  completionRequirements,
  onAssessmentSubmitted,
  onOpenCourseContent,
  onOpenAIAssistant,
  onPrevious,
  onNext,
  nextLocked
}: UnifiedPlayerViewProps) {
  // State to manage the active tab: 'content' or 'forum'
  const [activeTab, setActiveTab] = useState<'content' | 'forum'>('content');
  // On phones the outline card scrolls away above the lesson; each new lesson
  // starts with it back in view.
  const scrollPaneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (viewType === 'course' || viewType === 'module' || viewType === 'content') {
      setActiveTab('content');
    }
    scrollPaneRef.current?.scrollTo({ top: 0 });
  }, [viewType, contentItem, selectedModule]);
  if (loadingModuleContent) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading content...</p>
        </div>
      </div>
    );
  }
const renderMainView = () => {
    switch (viewType) {
      case 'course':
        return <CourseOverviewView course={course} onSelectModule={onSelectModule} progressData={progressData} isScormCourse={isScormCourse} completionRequirements={completionRequirements} />;
      case 'module':
        return <ModuleOverviewView module={moduleContent} onSelectContentItem={onSelectContentItem} progressData={progressData} isScormCourse={isScormCourse} />;
      case 'content':
        if (!contentItem) return null;
        return (
          <ContentDisplay
                  contentItem={contentItem}
                  selectedModule={selectedModule}
                  moduleContent={moduleContent}
                  currentVideoScene={currentVideoScene || 0}
                  currentAudioScene={currentAudioScene || 0}
                  isVideoPlaying={isVideoPlaying || false}
                  isAudioPlaying={isAudioPlaying || false}
                  videoPlaybackRate={videoPlaybackRate || 1}
                  audioPlaybackRate={audioPlaybackRate || 1}
                  videoVolume={videoVolume || 1}
                  audioVolume={audioVolume || 1}
                  autoPlayNext={autoPlayNext || false}
                  onVideoSceneChange={onVideoSceneChange || (() => {})}
                  onAudioSceneChange={onAudioSceneChange || (() => {})}
                  onVideoPlayPause={onVideoPlayPause || (() => {})}
                  onVideoPlayStateChange={onVideoPlayStateChange}
                  onVideoProgressRatio={onVideoProgressRatio}
                  videoResumeRatio={videoResumeRatio}
                  videoResumeAutoPlay={videoResumeAutoPlay}
                  onAudioPlayPause={onAudioPlayPause || (() => {})}
                  onVideoPlaybackRateChange={onVideoPlaybackRateChange || (() => {})}
                  onAudioPlaybackRateChange={onAudioPlaybackRateChange || (() => {})}
                  onVideoVolumeChange={onVideoVolumeChange || (() => {})}
                  onAudioVolumeChange={onAudioVolumeChange || (() => {})}
                  onAutoPlayToggle={onAutoPlayToggle || (() => {})}
                  onModuleClick={() => onSelectModule?.(selectedModule)}
                  onContentItemClick={(item) => onSelectContentItem?.(item)}
                  onProgressChange={onProgressChange}
                  onContentComplete={() => onContentComplete?.(contentItem._id)}
                  onAssessmentSubmitted={onAssessmentSubmitted}
                  progressData={progressData}
                  isPreview={!isLearner}
                />

        );
      default:
        return <WelcomeView course={course} />;
    }
  };

  // Phone/tablet outline card: lessons completed in the open module, or
  // modules completed in the course when no module is open (or a course-level
  // pre/post assessment is, as those belong to no module).
  const outlineCard = (() => {
    if (!selectedModule || contentItem?.assessment_level === 'COURSE') {
      const total = progressData?.total_modules || course?.modules?.length || 0;
      return { prefix: '', title: course?.title || course?.name || 'Course content', done: progressData?.completed_modules || 0, total, unit: 'module' };
    }
    const items: ContentItem[] = moduleContent?.module?.content_items || selectedModule.content_items || [];
    // Count lessons only; a module holding nothing but assessments counts those.
    const lessonItems = items.filter(item => item.type !== 'assessment');
    const counted = lessonItems.length ? lessonItems : items;
    const tracked = progressData?.module_progress?.find(mp => mp.module_id === selectedModule._id)?.lesson_progress || [];
    const done = counted.filter(item => tracked.some(lp => lp.lesson_id === item._id && lp.status === 'COMPLETED')).length;
    const isCourseAssessment = selectedModule.is_course_assessment || selectedModule.is_course_pre_assessment;
    const number = selectedModule.order ?? selectedModule.module_number; // same order as the breadcrumb
    return {
      prefix: !isCourseAssessment && number ? `Module ${number}: ` : '',
      title: selectedModule.title || 'Course content',
      done,
      total: counted.length,
      unit: lessonItems.length ? 'lesson' : 'assessment',
    };
  })();
  // Previous / Next only make sense while the lesson itself is on screen.
  const showStepper = activeTab === 'content' && !!(onPrevious || onNext);
  const outlinePercent = outlineCard.total ? Math.round((outlineCard.done / outlineCard.total) * 100) : 0;
  // Phones & tablets (< lg): the sidebar isn't beside the player, so the
  // course outline opens from this card — it shows the open module's
  // progress rather than a button floating over the lesson. Content tab only.
  const outlineCardElement = activeTab === 'content' && onOpenCourseContent ? (
    <div className="lg:hidden flex-shrink-0 px-4 py-2 bg-white border-b border-gray-200">
      <button
        onClick={onOpenCourseContent}
        aria-label="Open course content"
        className="w-full flex items-center gap-3 px-3 py-2 text-left bg-primary-50 border border-primary-100 rounded-xl hover:bg-primary-100/60 active:scale-[0.99] transition-all"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {outlineCard.prefix && <span className="text-primary-700">{outlineCard.prefix}</span>}
            {outlineCard.title}
          </p>
          {outlineCard.total > 0 && (
            <>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-600 truncate">
                  {outlineCard.done} of {outlineCard.total} {outlineCard.unit}{outlineCard.total === 1 ? '' : 's'} completed
                </span>
                <span className="flex-shrink-0 font-semibold text-primary-700">{outlinePercent}%</span>
              </div>
              <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary-600 rounded-full transition-all" style={{ width: `${outlinePercent}%` }} />
              </div>
            </>
          )}
        </div>
        <span className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center bg-white rounded-full shadow-sm">
          <ChevronRight className="w-4 h-4 text-primary-700" />
        </span>
      </button>
    </div>
  ) : null;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Always show tab navigation */}
      <div className="flex-shrink-0 border-b border-gray-200">
        <div className="flex items-center">
          <TabButton
            label="Content"
            icon={<BookOpen className="w-4 h-4" />}
            isActive={activeTab === 'content'}
            onClick={() => setActiveTab('content')}
          />
          {/* Only show Course Forum tab for learners, hide for admin */}
          {isLearner && (
            <TabButton
              label="Course Forum"
              icon={<MessageSquare className="w-4 h-4" />}
              isActive={activeTab === 'forum'}
              onClick={() => setActiveTab('forum')}
            />
          )}
          {/* Phones & tablets: AI Assistant lives at the end of the tab row. */}
          {onOpenAIAssistant && (
            <button
              onClick={onOpenAIAssistant}
              className="lg:hidden ml-auto mr-3 flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold text-primary-700 bg-white border border-primary-600 rounded-full hover:bg-primary-50 active:scale-95 transition-all"
            >
              <Brain className="w-4 h-4" />
              Ask AI
            </button>
          )}
        </div>
      </div>

      {/* Tab content. On phones & tablets the outline card scrolls away with
          the page, so a lesson or assessment gets the whole screen. A size
          container, so a lesson can size itself to exactly this pane's height
          (100cqh) whatever sits above it. */}
      <div ref={scrollPaneRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain [container-type:size]">
        {outlineCardElement}
        {activeTab === 'content' && renderMainView()}
        {activeTab === 'forum' && isLearner && (
          <div className="bg-gray-50 h-full">
            <ForumThreads courseId={courseId} courseName={course?.name} />
          </div>
        )}
      </div>
      {/* Phones & tablets: Previous / Next pinned below the lesson. They walk the
          sidebar's order and obey its locks (a locked step shows the lock toast). */}
      {showStepper && (
        <div className="lg:hidden flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-white border-t border-gray-200 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <button
            onClick={onPrevious}
            disabled={!onPrevious}
            className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1 px-2 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <button
            onClick={onNext}
            disabled={!onNext}
            className={`flex-1 min-h-[44px] inline-flex items-center justify-center gap-1 px-2 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              nextLocked ? 'text-gray-500 bg-gray-100 border border-gray-200' : 'text-white bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {nextLocked && <Lock className="w-4 h-4" />}
            Next
            {!nextLocked && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );


// Course Overview View Component
function CourseOverviewView({ course, onSelectModule: _onSelectModule, progressData, isScormCourse, completionRequirements }: { course: any; onSelectModule?: (module: any) => void; progressData?: DetailedProgress | null; isScormCourse?: boolean; completionRequirements?: CourseCompletionRequirementStatus | null }) {
  return (
    <div className=" bg-gray-50">
      {/* Course Header - Unified Style */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <h1 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{course?.title}</h1>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            {/* SCORM: completion is CMI/manual-based, so show spent/completed time instead of estimated time */}
            {isScormCourse ? (
              <TimeSpentBadge
                seconds={progressData?.total_time_spent_seconds}
                completed={progressData?.status === 'COMPLETED'}
              />
            ) : (
              <>
                {course?.estimated_duration?.total && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      {course.estimated_duration.total.hours || 0}h {course.estimated_duration.total.minutes || 0}m
                    </span>
                  </div>
                )}
                {/* AI courses: also show actual spent/completed time at the course level. */}
                {!!progressData?.total_time_spent_seconds && progressData.total_time_spent_seconds > 0 && (
                  <TimeSpentBadge
                    seconds={progressData.total_time_spent_seconds}
                    completed={progressData?.status === 'COMPLETED'}
                  />
                )}
              </>
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <BookOpen className="w-4 h-4" />
              <span>
                {(() => {
                  const moduleCount = course?.modules?.length || 0;

                  // Count all lessons
                  let totalLessons = 0;
                  course?.modules?.forEach((module: any) => {
                    totalLessons += (module.lessons || []).length;
                  });

                  // Count all assessments at all levels
                  let totalAssessments = 0;

                  // Course-level assessments
                  if (course?.pre_assessment) totalAssessments++;
                  if (course?.post_assessment) totalAssessments++;

                  // Module and content-level assessments
                  course?.modules?.forEach((module: any) => {
                    // Module-level assessments - check if arrays exist
                    if (Array.isArray(module.pre_assessments)) {
                      totalAssessments += module.pre_assessments.length;
                    }
                    if (Array.isArray(module.post_assessments)) {
                      totalAssessments += module.post_assessments.length;
                    }

                    // Content/lesson-level assessments
                    if (Array.isArray(module.lessons)) {
                      module.lessons.forEach((lesson: any) => {
                        if (Array.isArray(lesson.pre_assessments)) {
                          totalAssessments += lesson.pre_assessments.length;
                        }
                        if (Array.isArray(lesson.post_assessments)) {
                          totalAssessments += lesson.post_assessments.length;
                        }
                      });
                    }
                  });

                  return (
                    <>
                      {moduleCount} {moduleCount === 1 ? 'Module' : 'Modules'}
                      {totalLessons > 0 && (
                        <span className="ml-1">• {totalLessons} {totalLessons === 1 ? 'Lesson' : 'Lessons'}</span>
                      )}
                      {totalAssessments > 0 && (
                        <span className="ml-1">• {totalAssessments} {totalAssessments === 1 ? 'Assessment' : 'Assessments'}</span>
                      )}
                    </>
                  );
                })()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      {/* Course Description */}
      {course?.description && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">About This Course</h3>
          <p className="text-gray-700 leading-relaxed">{course.description}</p>
        </div>
      )}

      {/* Learning Objectives */}
      {course?.learning_objectives && course.learning_objectives.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-brand-purple-600" />
            Learning Objectives
          </h2>
          <div className="space-y-3">
            {course.learning_objectives.map((objective: any, index: number) => (
              <div key={objective.id || index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-brand-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-brand-purple-600">{index + 1}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {objective.description || objective.text || objective.objective}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Course Structure */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Course Structure</h2>
        <div className="space-y-3">
          {/* Course Pre-Assessment */}
          {course?.pre_assessment && (
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">
                      {course.pre_assessment.title || 'Course Pre-Assessment'}
                    </h3>
                  </div>
                  {course.pre_assessment.description && (
                    <p className="text-sm text-blue-700 line-clamp-2">{course.pre_assessment.description}</p>
                  )}
                </div>
                {course.pre_assessment.time_limit_minutes && (
                  <div className="flex items-center gap-1 text-sm text-blue-600 ml-4 flex-shrink-0">
                    <Clock className="w-4 h-4" />
                    {course.pre_assessment.time_limit_minutes}m
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Regular Modules with nested content */}
          {course?.modules?.map((module: any) => {
            // Count lessons and assessments
            const lessons = module.lessons || [];
            const modulePreAssessments = module.pre_assessments || [];
            const modulePostAssessments = module.post_assessments || [];

            // Count content-level assessments within lessons
            let contentPreAssessments = 0;
            let contentPostAssessments = 0;
            lessons.forEach((lesson: any) => {
              if (lesson.pre_assessments) contentPreAssessments += lesson.pre_assessments.length;
              if (lesson.post_assessments) contentPostAssessments += lesson.post_assessments.length;
            });

            const totalLessons = lessons.length;
            const totalAssessments = modulePreAssessments.length + modulePostAssessments.length + contentPreAssessments + contentPostAssessments;

            return (
              <div
                key={module._id}
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Module {module.module_number}: {module.title}
                    </h3>
                    {module.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">{module.description}</p>
                    )}
                    {/* Content count */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {totalLessons > 0 && (
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3.5 h-3.5" />
                          {totalLessons} {totalLessons === 1 ? 'Lesson' : 'Lessons'}
                        </span>
                      )}
                      {totalAssessments > 0 && (
                        <span className="flex items-center gap-1">
                          <ClipboardCheck className="w-3.5 h-3.5" />
                          {totalAssessments} {totalAssessments === 1 ? 'Assessment' : 'Assessments'}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* SCORM: no time shown in the course structure (completion is CMI/manual) */}
                  {!isScormCourse && module.estimated_duration && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 ml-4 flex-shrink-0">
                      <Clock className="w-4 h-4" />
                      {module.estimated_duration.hours || 0}h {module.estimated_duration.minutes || 0}m
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Course Post-Assessment */}
          {course?.post_assessment && (
            <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardCheck className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">
                      {course.post_assessment.title || 'Final Course Assessment'}
                    </h3>
                  </div>
                  {course.post_assessment.description && (
                    <p className="text-sm text-purple-700 line-clamp-2">{course.post_assessment.description}</p>
                  )}
                </div>
                {course.post_assessment.time_limit_minutes && (
                  <div className="flex items-center gap-1 text-sm text-purple-600 ml-4 flex-shrink-0">
                    <Clock className="w-4 h-4" />
                    {course.post_assessment.time_limit_minutes}m
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Certificate requirements — which assessments must be passed before this
          course counts as completed. Sits below the structure so the learner has
          just seen the assessments being referred to. */}
      {completionRequirements && completionRequirements.requirements.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-900">To earn your certificate</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Finish every lesson and pass the {completionRequirements.requirements.length === 1 ? 'assessment' : 'assessments'} below,
            in any order. The course is marked complete once both are done.
          </p>

          <ul className="space-y-2">
            {completionRequirements.requirements.map((req) => (
              <li
                key={req.assessment_id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                  req.passed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                {req.passed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${req.passed ? 'text-green-900' : 'text-gray-900'}`}>
                    {req.title}
                  </p>
                  <p className={`text-xs ${req.passed ? 'text-green-700' : 'text-gray-500'}`}>
                    {req.assessment_level === 'COURSE'
                      ? 'Course'
                      : req.assessment_level === 'MODULE'
                      ? 'Module'
                      : 'Lesson'}{' '}
                    {req.assessment_type === 'PRE' ? 'pre-assessment' : 'post-assessment'}
                  </p>
                </div>
                <span
                  className={`text-xs font-semibold flex-shrink-0 ${
                    req.passed ? 'text-green-700' : 'text-gray-400'
                  }`}
                >
                  {req.passed ? 'Passed' : 'Not passed yet'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Course Info Cards */}
      {(course?.category_name || course?.target_audience) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Category */}
          {course?.category_name && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Category</h3>
              <p className="text-gray-900 font-medium">{course.category_name}</p>
            </div>
          )}

          {/* Target Audience */}
          {course?.target_audience && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Target Audience</h3>
              <p className="text-gray-900 font-medium">{course.target_audience}</p>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

// Module Overview View Component
function ModuleOverviewView({ module, onSelectContentItem, progressData, isScormCourse }: { module: any; onSelectContentItem?: (item: ContentItem) => void; progressData?: DetailedProgress | null; isScormCourse?: boolean }) {
  if (!module?.module) return null;

  const moduleData = module.module;

  // Get module progress from tracking data
  const moduleProgress = progressData?.module_progress?.find(mp => mp.module_id === moduleData._id);

  return (
    <div className=" bg-gray-50">
      {/* Module Header - Unified Style */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          {/* Breadcrumb */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 mb-2 min-w-0">
            <span className="font-medium">Module {moduleData.order ?? moduleData.module_number}</span>
            <span className="text-gray-400">›</span>
            <span className="text-gray-900 font-semibold break-words min-w-0">{moduleData.title}</span>
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {/* SCORM: no estimated time (completion is CMI/manual) — only the spent/completed time below */}
            {!isScormCourse && moduleData.estimated_duration && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Clock className="w-4 h-4" />
                <span>Estimated Time: {formatSecondsToHMS(((moduleData.estimated_duration.hours || 0) * 3600) + ((moduleData.estimated_duration.minutes || 0) * 60))}</span>
              </div>
            )}
            {moduleProgress && moduleProgress.time_spent_seconds > 0 && (() => {
              const isCompleted = moduleProgress.status === 'COMPLETED';
              return (
                <div className={`flex items-center gap-1.5 text-xs ${isCompleted ? 'text-green-600' : 'text-blue-500'}`}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  <span>{isCompleted ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(moduleProgress.time_spent_seconds)}</span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      {/* Module Description */}
      {moduleData.description && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">About This Module</h3>
          <p className="text-gray-700 leading-relaxed">{moduleData.description}</p>
        </div>
      )}

      {/* Learning Objectives */}
      {moduleData.learning_objectives && moduleData.learning_objectives.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-brand-purple-600" />
            Learning Objectives
          </h2>
          <div className="space-y-3">
            {moduleData.learning_objectives.map((objective: any, index: number) => (
              <div key={objective.id || index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-brand-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-brand-purple-600">{index + 1}</span>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {objective.description || objective.text || objective.objective}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Items with Hierarchy */}
      {moduleData.content_items && moduleData.content_items.length > 0 && (() => {
        // Separate module-level assessments from regular content
        const regularContent: ContentItem[] = [];
        const moduleAssessments: ContentItem[] = [];

        moduleData.content_items.forEach((item: ContentItem) => {
          if (item.type === 'assessment') {
            // Check if it's a module-level assessment (contains "Module" in title)
            const isModuleAssessment = item.title?.toLowerCase().includes('module');
            if (isModuleAssessment) {
              moduleAssessments.push(item);
            } else {
              regularContent.push(item);
            }
          } else {
            regularContent.push(item);
          }
        });

        return (
          <>
            {/* Lessons and Lesson Assessments */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Learning Content</h2>
              <div className="space-y-2">
                {(() => {
                  let lessonNumber = 0;
                  return regularContent.map((item: ContentItem) => {
                    const isLesson = item.type !== 'assessment';
                    if (isLesson) lessonNumber++;

                    return (
                      <div
                        key={item._id}
                        className={`w-full p-4 rounded-lg ${
                          item.type === 'assessment'
                            ? 'bg-brand-blue-50 border border-brand-blue-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {isLesson ? (
                                <span className="text-xs font-semibold text-gray-500">
                                  Lesson {lessonNumber}
                                </span>
                              ) : (
                                <span className="text-xs font-semibold text-brand-blue-600">
                                  Post-Assessment
                                </span>
                              )}
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                            {item.description && (
                              <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                            )}
                          </div>
                          {/* SCORM: no time per lesson in the list — only in the top header. */}
                          {!isScormCourse && (item.duration || item.estimated_time) && (
                            <div className="flex items-center gap-1 text-sm text-gray-500 ml-4">
                              <Clock className="w-4 h-4" />
                              {(item.duration || item.estimated_time)?.hours || 0}h {(item.duration || item.estimated_time)?.minutes || 0}m
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Module-Level Assessments - Separate Card */}
            {moduleAssessments.length > 0 && (
              <div className="bg-gradient-to-br from-brand-blue-50 to-brand-indigo-50 rounded-xl border-2 border-brand-blue-300 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <ClipboardCheck className="w-6 h-6 text-brand-blue-600" />
                  Module Assessment
                </h2>
                <p className="text-sm text-gray-600 mb-4">Complete this assessment to test your understanding of the entire module</p>
                <div className="space-y-2">
                  {moduleAssessments.map((item: ContentItem) => (
                    <button
                      key={item._id}
                      onClick={() => onSelectContentItem?.(item)}
                      className="w-full text-left p-4 bg-white hover:bg-brand-blue-50 border border-brand-blue-200 hover:border-brand-blue-400 rounded-lg transition-all shadow-sm"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                          {item.description && (
                            <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                        {(item.duration || item.estimated_time) && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 ml-4">
                            <Clock className="w-4 h-4" />
                            {(item.duration || item.estimated_time)?.hours || 0}h {(item.duration || item.estimated_time)?.minutes || 0}m
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}
      </div>
    </div>
  );
}

// Welcome View Component
function WelcomeView({ course }: { course: any }) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center max-w-2xl mx-4">
        <div className="w-24 h-24 bg-gradient-to-br from-brand-blue-500 to-brand-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <BookOpen className="w-12 h-12 text-white" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 break-words">
          Welcome to {course?.title || 'Your Course'}
        </h2>
        <p className="text-base sm:text-lg text-gray-600 mb-6">
          Click on the course progress or select a module to begin your learning journey
        </p>
      </div>
    </div>
  );
}
}